import { useState, useRef, useEffect } from "react";
import { Search, X, Check, ChevronDown } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";

export default function SearchSelect({
  items, value, onChange, labelKey = "nome",
  placeholder = "Selecionar...", emptyText = "Nenhum item encontrado.",
  renderItem = null,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50  = appearance.colors?.["50"]  || "#EFF6FF";

  const selected = items.find(i => i.id === value);
  const filtered = items.filter(item =>
    (item[labelKey] || "").toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false); setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const handleOpen = () => {
    if (!open && containerRef.current) {
      const r = containerRef.current.getBoundingClientRect();
      setDropUp(r.bottom + 260 > window.innerHeight);
    }
    setOpen(prev => !prev);
    setSearch("");
  };

  const handleSelect = (id) => {
    onChange(id); setOpen(false); setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation(); onChange(""); setSearch("");
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <div
        onClick={handleOpen}
        className="flex items-center gap-2 w-full min-h-9 px-3 py-1 rounded-md border border-slate-300 bg-white cursor-pointer hover:border-slate-400 transition-colors overflow-hidden"
      >
        {selected ? (
          <>
            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-semibold"
              style={{ backgroundColor: tc500 }}>
              {selected[labelKey]?.charAt(0).toUpperCase()}
            </div>
            <span className="text-sm font-medium text-slate-800 flex-1 truncate">{selected[labelKey]}</span>
            <button type="button" onClick={handleClear} className="text-slate-400 hover:text-slate-600 flex-shrink-0">
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-sm text-slate-400 flex-1 truncate">{placeholder}</span>
          </>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div className={`absolute z-50 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-xl ${dropUp ? "bottom-full mb-1" : "top-full mt-1"}`}>
          {/* Search inside dropdown */}
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                ref={searchRef}
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={placeholder}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md outline-none focus:border-slate-400 bg-white"
              />
            </div>
          </div>
          {/* Items list */}
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-400 text-center">{emptyText}</div>
            ) : filtered.map(item => {
              const isSelected = value === item.id;
              return (
                <button key={item.id} type="button" onClick={() => handleSelect(item.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-0"
                  style={isSelected ? { backgroundColor: tc50 } : undefined}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-semibold flex-shrink-0"
                    style={{ backgroundColor: isSelected ? tc500 : "#e2e8f0", color: isSelected ? "#fff" : "#64748b" }}>
                    {item[labelKey]?.charAt(0).toUpperCase()}
                  </div>
                  {renderItem ? (
                    <div className="flex-1 min-w-0">{renderItem(item, isSelected)}</div>
                  ) : (
                    <span className={`flex-1 truncate ${isSelected ? "font-medium text-slate-900" : "text-slate-700"}`}>
                      {item[labelKey]}
                    </span>
                  )}
                  {isSelected && <Check className="w-4 h-4 flex-shrink-0" style={{ color: tc500 }} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
